import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import UssdInput from './UssdInput';
import { useSiteChat } from '../contexts/ChatContext';

/**
 * The site's signature element: a USSD session.
 *
 * Dennis builds USSD services for feature phones, where the whole interface is
 * a numbered menu and you reply with a digit. So the hero is one — its options
 * are the site's real navigation, option 0 opens the assistant, and the reply
 * line at the bottom genuinely takes a reply. `CON` is the protocol prefix for
 * a session that stays open.
 */

const MENU = [
  { key: '1', label: 'Writing', to: '/posts', note: 'notes on shipping' },
  { key: '2', label: 'Work', to: '/#work', note: 'what I have built' },
  { key: '3', label: 'About', to: '/about', note: 'ten years, two continents' },
  { key: '0', label: 'Ask me anything', chat: true, note: 'my assistant, live', live: true },
];

export const USSD_MENU = MENU;

export function LiveDot({ className = '', pulse = true }) {
  return (
    <span className={`relative flex h-1.5 w-1.5 ${className}`} aria-hidden="true">
      {pulse && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-panel-signal opacity-60" />
      )}
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-panel-signal" />
    </span>
  );
}

function Line({ children, index, animate, className = '' }) {
  return (
    <div
      className={className}
      style={animate ? { animation: `rise 0.5s cubic-bezier(0.16,1,0.3,1) ${0.25 + index * 0.08}s both` } : undefined}
    >
      {children}
    </div>
  );
}

function UssdPanel() {
  const navigate = useNavigate();
  const { ask, openChat } = useSiteChat();
  const reduceMotion = useReducedMotion();
  const animate = !reduceMotion;
  const [armed, setArmed] = useState(false);
  const regionRef = useRef(null);

  const choose = (item) => {
    if (item.chat) openChat();
    else navigate(item.to);
  };

  // Reply with a number, the way you would on the handset.
  useEffect(() => {
    if (!armed) return undefined;

    const onKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target && event.target.tagName === 'INPUT') return;
      const match = MENU.find((item) => item.key === event.key);
      if (!match) return;
      event.preventDefault();
      choose(match);
    };

    const node = regionRef.current;
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [armed, navigate, openChat]);

  // A number goes to that option; anything else becomes a question for the assistant.
  const onReply = (value) => {
    const match = MENU.find((item) => item.key === value);
    if (match) choose(match);
    else ask(value);
  };

  return (
    <div
      ref={regionRef}
      tabIndex={-1}
      onFocus={() => setArmed(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setArmed(false);
      }}
      onMouseEnter={() => setArmed(true)}
      onMouseLeave={() => setArmed(false)}
      aria-label="Site menu, USSD style"
      className="relative rounded-sm border border-black/40 bg-panel p-6 font-mono text-sm text-panel-foreground shadow-[0_1px_0_0_hsl(var(--rule))] dark:border-white/10 dark:shadow-none sm:p-8"
    >
      <Line index={0} animate={animate} className="flex items-center justify-between text-eyebrow uppercase tracking-[0.18em] text-panel-muted">
        <span>USSD session</span>
        <span className="flex items-center gap-1.5">
          <LiveDot pulse={false} />
          {armed ? 'listening' : 'open'}
        </span>
      </Line>

      <div className="mt-5 space-y-0.5 leading-relaxed">
        <Line index={1} animate={animate}>
          <span className="text-panel-signal">CON</span>{'  '}Dennis M. Nyabuti
        </Line>
        <Line index={2} animate={animate} className="pl-[2.6ch] text-panel-muted">
          Senior Software Engineer
        </Line>
        <Line index={3} animate={animate} className="pl-[2.6ch] text-panel-muted">
          Kigali &middot; since 2013
        </Line>
      </div>

      <ul className="mt-6 space-y-1">
        {MENU.map((item, i) => (
          <li key={item.key}>
            <Line index={4 + i} animate={animate}>
              <button
                type="button"
                onClick={() => choose(item)}
                className={`group flex w-[calc(100%+1rem)] items-baseline gap-3 -mx-2 rounded-sm px-2 py-1 text-left transition-colors hover:bg-white/[0.07] focus-visible:bg-white/[0.07] ${
                  item.live ? 'bg-white/[0.04]' : ''
                }`}
              >
                <span className="tabular-nums text-panel-signal">{item.key}.</span>
                <span className={item.live ? 'text-panel-signal' : 'text-panel-foreground'}>{item.label}</span>
                {item.live && <LiveDot className="ml-1.5 self-center" />}
                <span className="ml-auto hidden text-xs text-panel-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 sm:inline">
                  {item.note}
                </span>
              </button>
            </Line>
          </li>
        ))}
      </ul>

      <Line index={8} animate={animate} className="mt-6 border-t border-white/10 pt-4">
        <UssdInput
          onSubmit={onReply}
          label="hero"
          placeholder={"Reply 0–3, or just ask a question"}
        />
      </Line>
    </div>
  );
}

export default UssdPanel;
